import React from 'react'
import { SourceIconSpec, SourceIconStyle, categoryHue, useSourceIcons } from './sourceIconStore'
import s from './SourceIcon.module.css'

/*
 * A source's icon from the shared set (sourceIconStore): a tile tinted by the
 * source's category, with the glyph, the app's own logo or two letters, and
 * an optional corner badge — or, in a 'bare' collection (the original
 * icons), the picture alone. Light or dark follows the nearest
 * [data-theme="dark"] — the v2 pages' theme switch — unless `mode` forces one
 * (the editor's side-by-side preview). A source the set does not know gets
 * its first two letters on a grey tile, never an empty gap.
 */
export function SourceIcon({ source, size = 20, className, title, fallback }: {
  source: string
  size?: number
  className?: string
  title?: string
  /**
   * The caller's old icon. The 'text' collection is "as it was": with one
   * given, it is what shows there, so switching collections shows exactly
   * what changed.
   */
  fallback?: React.ReactNode
}) {
  const set = useSourceIcons()
  const spec = set?.icons[source]
  if (fallback !== undefined && set?.style === 'text') return <>{fallback}</>
  return (
    <IconTile
      spec={spec ?? (set ? { category: '', text: initials(source) } : null)}
      hue={spec ? categoryHue(set, spec.category) : undefined}
      look={spec ? set?.style : undefined}
      size={size}
      className={className}
      title={title ?? spec?.label ?? source}
    />
  )
}

const initials = (source: string) => (source.replace(/[^A-Za-z0-9]/g, '').slice(0, 2) || '?').replace(/^./, (c) => c.toUpperCase())

/** the tile itself, from a spec — the editor previews unsaved specs with it */
export function IconTile({ spec, hue, look = 'tile', size = 20, className, title, mode }: {
  spec: SourceIconSpec | null
  hue?: number
  /** tile (default) · bare: the picture alone, full size · text: letters on spec.color, as the old result badges */
  look?: SourceIconStyle
  size?: number
  className?: string
  title?: string
  mode?: 'light' | 'dark'
}) {
  const bare = look === 'bare'
  const isText = look === 'text'
  const cls = [
    s.tile,
    isText ? s.textLook : '',
    hue === undefined ? s.grey : '',
    spec?.logo ? s.logo : '',
    bare ? s.bare : '',
    mode === 'light' ? s.forceLight : mode === 'dark' ? s.forceDark : '',
    className || '',
  ].join(' ')
  const style = {
    '--h': hue ?? 215, width: size, height: size, borderRadius: Math.round(size * 0.28),
    ...(isText ? { background: spec?.color || '#64748B' } : {}),
  } as React.CSSProperties
  const glyph = Math.round(size * 0.58)
  const badge = Math.max(9, Math.round(size * 0.5))
  return (
    <span className={cls} style={style} title={title} role="img" aria-label={title}>
      {spec?.svg && <span className={s.svg} style={{ width: glyph, height: glyph }} dangerouslySetInnerHTML={{ __html: spec.svg }} />}
      {spec?.logo && <img className={s.img} src={spec.logo} alt="" style={bare ? { width: size, height: size, borderRadius: 0 } : { width: Math.round(size * 0.84), height: Math.round(size * 0.84) }} />}
      {spec?.text && <span className={s.text} style={{ fontSize: Math.max(isText ? 7 : 8, Math.round(size * (isText ? 0.44 : 0.38))) }}>{spec.text}</span>}
      {spec?.badge && (
        <span className={s.badge} style={{ width: badge, height: badge, right: -Math.round(badge * 0.28), bottom: -Math.round(badge * 0.28) }}>
          {spec.badge.startsWith('<')
            ? <span className={s.svg} style={{ width: Math.round(badge * 0.66), height: Math.round(badge * 0.66) }} dangerouslySetInnerHTML={{ __html: spec.badge }} />
            : <span className={s.text} style={{ fontSize: Math.max(6, Math.round(badge * 0.45)) }}>{spec.badge}</span>}
        </span>
      )}
    </span>
  )
}
